import {
	createContext,
	type PropsWithChildren,
	useContext,
	useMemo,
} from 'react';
import type { Nullable } from '@gov-nx/core/types';
import { type PPPDetailContextTypes } from './service.types';
import { usePPPDetailQuery } from './hooks/usePPPDetailQuery';
import type { ServiceCode } from '../../definitions/codes';
import { usePPPLocale } from '../usePPPLocale';
import { nameFormatting, toDateReadable } from '@gov-nx/utils/common';
import { formatAddress } from '../utils';
import { useNavigate } from 'react-router-dom';
import { serviceSubRouteResolver } from '@gov-nx/web';
import { formatDateRange } from '../zapis/utils';
import {
	isComplainButtonVisible,
	isTerminateButtonVisible,
	resolveReason,
} from './utils';

export const PPPDetailContext =
	createContext<Nullable<PPPDetailContextTypes>>(null);

export const PPPDetailContextProvider = ({
	children,
	pppId,
	code,
}: PropsWithChildren<{ pppId: string; code: ServiceCode }>) => {
	const navigate = useNavigate();
	const query = usePPPDetailQuery({ pppId });
	const { t } = usePPPLocale();
	const empty = '-';

	const basicData = useMemo(
		() => [
			{
				label: t('detail.zadost.zakladni-udaje.datum-podani'),
				value: query.data?.zakladniUdaje?.datumPodani
					? toDateReadable(query.data.zakladniUdaje.datumPodani)
					: empty,
			},
			{
				label: t('detail.zadost.zakladni-udaje.id-zadosti'),
				value: query.data?.zakladniUdaje?.idZadosti ?? empty,
			},
		],
		[t, query.data]
	);

	const zadatelData = useMemo(
		() => [
			{
				label: t('detail.zadost.zadatel.jmeno-prijmeni'),
				value:
					query.data?.zadatel?.jmeno && query.data.zadatel.prijmeni
						? nameFormatting(
								query.data.zadatel.prijmeni,
								query.data.zadatel.jmeno
							)
						: empty,
			},
			{
				label: t('detail.zadost.zadatel.datum-narozeni'),
				value: query.data?.zadatel?.datumNarozeni
					? toDateReadable(query.data.zadatel.datumNarozeni)
					: empty,
			},
			{
				label: t('detail.zadost.zadatel.adresa-trvaleho-bydliste'),
				value: query.data?.zadatel?.adresa
					? formatAddress(query.data.zadatel.adresa)
					: empty,
			},
		],
		[t, query.data]
	);

	const prijemceData = useMemo(
		() => [
			{
				label: t('detail.zadost.prijemce.nazev'),
				value: query.data?.prijemce?.ovm?.nazev ?? empty,
			},
			{
				label: t('detail.zadost.prijemce.odpovedny-utvar'),
				value: query.data?.prijemce?.utvar ?? empty,
			},
			{
				label: t('detail.zadost.prijemce.specifikace-utvaru'),
				value: query.data?.prijemce?.utvarSpecifikace ?? empty,
			},
		],
		[t, query.data]
	);

	const detailZadostiData = useMemo(
		() => [
			{
				label: t('detail.zadost.detail-zadosti.typ-PPP'),
				value: query.data?.zadost?.typPppNazev ?? empty,
			},
			{
				label: t('detail.zadost.detail-zadosti.OVM'),
				value: query.data?.prijemce?.ovm?.nazev ?? empty,
			},
			{
				label: t('detail.zadost.detail-zadosti.vydany-dokument.label'),
				children: [
					{
						label: t('detail.zadost.detail-zadosti.vydany-dokument.cislo'),
						value: query.data?.zadost?.dokumentCislo ?? empty,
					},
					{
						label: t(
							'detail.zadost.detail-zadosti.vydany-dokument.datum-vydani'
						),
						value: query.data?.zadost?.datumVydani
							? toDateReadable(query.data.zadost.datumVydani)
							: empty,
					},
					{
						label: t(
							'detail.zadost.detail-zadosti.vydany-dokument.datum-platnosti-od-do'
						),
						value: formatDateRange(
							query.data?.zadost?.datumPlatnostiOd,
							query.data?.zadost?.datumPlatnostiDo
						),
					},
				],
			},
		],
		[t, query.data]
	);

	return (
		<PPPDetailContext.Provider
			value={{
				code,
				data: query.data ?? {},
				basicData,
				zadatelData,
				prijemceData,
				detailZadostiData,
				isLoading: query.isLoading,
				isError: query.isError,
				reason: resolveReason(
					query.data?.zakladniUdaje,
					t('detail.zadost.duvod-zamitnuti.nezadan')
				),
				isTerminateButtonVisible: isTerminateButtonVisible(
					query.data?.zakladniUdaje?.stav
				),
				isComplainButtonVisible: isComplainButtonVisible(
					query.data?.zakladniUdaje?.stav
				),
				onComplainClick: () =>
					navigate(
						serviceSubRouteResolver('PPP', 'reklamace', { pppId }, ['pppId'])
					),
				onTerminateClick: () =>
					navigate(
						serviceSubRouteResolver('PPP', 'ukonceni', { pppId }, ['pppId'])
					),
			}}>
			{children}
		</PPPDetailContext.Provider>
	);
};

export const usePPPDetailContext = (): PPPDetailContextTypes =>
	useContext(PPPDetailContext) as PPPDetailContextTypes;
